// apps/web/src/features/crm/leads/list/EngagementBucketChips.tsx
//
// Single-select bucket filter chips for the v4 lead list (#2247 FR-1). Driven by
// the server `counts` so switching a chip never zeroes the others (#2257 FR-1).
// The No-client bucket is one of these chips — a saved-list FILTER, not a table
// column or stage (BR-3 / P-4). Styled to match the shared `FiltersBar` chips;
// kept feature-local because `FiltersBar`'s `chips` type is multi-select and
// carries no per-chip counter.
import { useTranslation } from 'react-i18next';
import { EngagementBucket, type EngagementBucketCounts } from '@magimp/shared';
import {
  ENGAGEMENT_BUCKET_ORDER,
  ENGAGEMENT_SCOPE_BUCKET_ORDER,
  bucketCount,
} from './engagementBucketMeta.js';

export interface EngagementBucketChipsProps {
  active: EngagementBucket;
  counts: EngagementBucketCounts;
  onSelect: (bucket: EngagementBucket) => void;
  showUnassigned?: boolean;
}

export function EngagementBucketChips({
  active,
  counts,
  onSelect,
  showUnassigned = false,
}: EngagementBucketChipsProps) {
  const { t } = useTranslation(['leads']);
  // WHY: literal `t('leads:buckets.X')` per bucket — i18next's typed-key
  // contract rejects a runtime `t(dynamicKey)` (mirrors `deals/DealStageBadge`).
  const labels: Partial<Record<EngagementBucket, string>> = {
    [EngagementBucket.ALL]: t('leads:buckets.all'),
    [EngagementBucket.UNASSIGNED]: t('leads:buckets.unassigned'),
    [EngagementBucket.NEW]: t('leads:buckets.new'),
    [EngagementBucket.IN_WORK]: t('leads:buckets.inWork'),
    [EngagementBucket.CLOSING]: t('leads:buckets.closing'),
    [EngagementBucket.DEAL]: t('leads:buckets.deal'),
    [EngagementBucket.POSTPONED]: t('leads:buckets.postponed'),
    [EngagementBucket.NO_CLIENT]: t('leads:buckets.noClient'),
    [EngagementBucket.CLOSED]: t('leads:buckets.closed'),
  };
  return (
    <div
      role="group"
      aria-label={t('leads:filters.stage.label')}
      data-testid="engagement-bucket-chips"
      style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}
    >
      {(showUnassigned ? ENGAGEMENT_SCOPE_BUCKET_ORDER : ENGAGEMENT_BUCKET_ORDER).map((bucket) => {
        const on = bucket === active;
        const n = bucketCount(bucket, counts);
        return (
          <button
            key={bucket}
            type="button"
            aria-pressed={on}
            onClick={() => onSelect(bucket)}
            data-testid={`engagement-bucket-${bucket}`}
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: 6,
              height: 30,
              padding: '0 12px',
              borderRadius: 'var(--r-full)',
              background: on ? 'var(--accent-1-subtle)' : 'var(--bg-1)',
              color: on ? 'var(--accent-1-press)' : 'var(--fg-1)',
              border: '1px solid ' + (on ? 'transparent' : 'var(--border-1)'),
              cursor: 'pointer',
              fontSize: 13,
              fontWeight: on ? 600 : 400,
            }}
          >
            <span>{labels[bucket]}</span>
            <span
              style={{
                fontSize: 11,
                fontWeight: 600,
                color: on ? 'var(--accent-1-press)' : 'var(--fg-3)',
              }}
            >
              {n}
            </span>
          </button>
        );
      })}
    </div>
  );
}
